import { TernaryGeometry } from '../core/ternaryGeometry';
import { ColorMapping } from '../core/colorMapping';
import { CompositionUtils } from '../core/compositionUtils';
// TODO: check if this works correctly in various environments
//  for d3 (cf. inside TricoloreViz constructor too)
import { select } from 'd3-selection';
import { group } from 'd3-array';
/**
 * js visualization for Tricolore
 */
export class TricoloreViz {
    /**
     * Create a TricoloreViz instance
     *
     * @param selector - CSS selector for container or D3 selection
     * @param width - Width of the visualization
     * @param height - Height of the visualization
     * @param margin - Margins around the visualization
     *
     * @throws Error - If js is not available
     */
    constructor(selector, width = 650, height = 520, margin = {
        top: 20,
        right: 60,
        bottom: 50,
        left: 60,
    }) {
        this.canvas = null;
        this.ctx = null;
        this.container = select(selector);
        this.width = width;
        this.height = height;
        this.margin = margin;
        // Create SVG container
        this.svg = this.container.append('svg').attr('width', width).attr('height', height);
        // Create group for the triangle
        this.triangle = this.svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`).attr('class', 'em-tricolore-triangle');
        // Create group for legending elements (axis names and ticks)
        this.legend = this.svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`).attr('class', 'em-tricolore-legend-labels');
        // Create group for data points
        this.circles = this.svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`).attr('class', 'em-tricolore-data-points');
    }
    /**
     * Create a continuous ternary plot using canvas
     *
     * @param data - Array of ternary points
     * @param options - Visualization options
     *
     * @throws Error - If showData is true and data contains invalid ternary points
     */
    createContinuousPlot(data = [], options = {}) {
        var _a, _b;
        const { center = [1 / 3, 1 / 3, 1 / 3], hue = 80, chroma = 140, lightness = 80, contrast = 0.4, spread = 1, showData = true, showCenter = true, showLines = true, labels = ['p₁', 'p₂', 'p₃'], labelPosition = 'corner', colorTarget = 'triangles', cornerLabelOffset, } = options;
        const plotWidth = this.width - this.margin.left - this.margin.right;
        const plotHeight = this.height - this.margin.top - this.margin.bottom;
        const size = Math.min(plotWidth, plotHeight);
        // Remove any existing canvas
        if (this.canvas) {
            select(this.canvas).remove();
        }
        // Clear previous contents
        this.triangle.selectAll('*').remove();
        this.legend.selectAll('*').remove();
        this.circles.selectAll('*').remove();
        // Create canvas for continuous color rendering
        this.canvas = document.createElement('canvas');
        this.canvas.width = size;
        this.canvas.height = size;
        this.ctx = this.canvas.getContext('2d');
        if (!this.ctx)
            return;
        // Draw the colored triangle on canvas
        this.drawContinuousTriangle(size, center, hue, chroma, lightness, contrast, spread);
        // Position canvas
        this.triangle.append('image').attr('x', 0).attr('y', 0).attr('width', size).attr('height', size).attr('href', this.canvas.toDataURL());
        // Add triangle border and axes using SVG
        this.drawTriangleFrame(size, labels, center, showCenter, showLines, labelPosition, colorTarget, undefined, cornerLabelOffset);
        // Add data points if requested
        if (showData && data.length > 0) {
            const colorTarget = (_a = options.colorTarget) !== null && _a !== void 0 ? _a : 'triangles';
            this.addDataPoints(data, size, options.dataPointHandlers, showCenter, center, undefined, colorTarget);
        }
        if (showCenter) {
            this.addCenterAnnotation(size, center, (_b = options.centerLabel) !== null && _b !== void 0 ? _b : 'Average value', options.centerAnnotationOffsets);
        }
        // Reliable mouseleave on the SVG root — fires when pointer exits the entire legend
        if (options.onLeave) {
            this.svg.on('mouseleave', () => options.onLeave());
        }
    }
    /**
     * Create a discrete ternary plot using SVG polygons
     *
     * @param data - Array of ternary points
     * @param options - Visualization options
     *
     * @throws Error - If showData is true and data contains invalid ternary points
     */
    createDiscretePlot(data = [], options = {}) {
        var _a;
        const { center = [1 / 3, 1 / 3, 1 / 3], breaks = 4, hue = 80, chroma = 140, lightness = 80, contrast = 0.4, spread = 1, showData = true, showCenter = true, showLines = true, labels = ['p₁', 'p₂', 'p₃'], labelPosition = 'corner', colorTarget = 'triangles', cornerLabelOffset, } = options;
        const plotWidth = this.width - this.margin.left - this.margin.right;
        const plotHeight = this.height - this.margin.top - this.margin.bottom;
        const size = Math.min(plotWidth, plotHeight);
        // Clear previous contents
        this.triangle.selectAll('*').remove();
        this.legend.selectAll('*').remove();
        this.circles.selectAll('*').remove();
        // Generate mesh centroids and vertices
        const centroids = TernaryGeometry.ternaryMeshCentroids(breaks);
        const vertices = TernaryGeometry.ternaryMeshVertices(centroids);
        // Calculate colors for each centroid
        const centroidPoints = centroids.map((c) => [c.p1, c.p2, c.p3]);
        const colors = ColorMapping.colorMapTricolore(centroidPoints, center, breaks, hue, chroma, lightness, contrast, spread);
        // Group vertices by triangle id
        const triangleGroups = group(vertices, (d) => d.id);
        // Create a polygon for each triangle
        triangleGroups.forEach((triangleVertices, id) => {
            const points = triangleVertices
                .map((v) => {
                const [x, y] = this.ternaryToSvgCoords([v.p1, v.p2, v.p3], size);
                return `${x},${y}`;
            })
                .join(' ');
            const color = colors[Number(id) - 1].rgb;
            const classIndex = Number(id) - 1;
            const poly = this.triangle
                .append('polygon')
                .attr('points', points)
                .attr('stroke', 'none')
                .attr('classIndex', classIndex)
                .attr('class', 'em-ternary-discrete-polygon');
            if (colorTarget === 'triangles') {
                poly.attr('fill', color);
            }
            else {
                poly.attr('fill', '#fffefe'); // or 'none'
            }
            const handlers = options.triangleHandlers;
            if (handlers === null || handlers === void 0 ? void 0 : handlers.mouseover) {
                poly.on('mouseover', (e) => handlers.mouseover(e, color));
            }
            if (handlers === null || handlers === void 0 ? void 0 : handlers.mouseout) {
                poly.on('mouseout', (e) => handlers.mouseout(e));
            }
        });
        // Draw triangle border and axes
        this.drawTriangleFrame(size, labels, center, showCenter, showLines, labelPosition, colorTarget, breaks, cornerLabelOffset);
        // Add data points if requested
        if (showData && data.length > 0) {
            const pointColors = colorTarget === 'points' ? ColorMapping.colorMapTricolore(data, center, breaks, hue, chroma, lightness, contrast, spread) : undefined;
            this.addDataPoints(data, size, options.dataPointHandlers, showCenter, center, colorTarget === 'points' ? pointColors : undefined, colorTarget);
        }
        if (showCenter) {
            this.addCenterAnnotation(size, center, (_a = options.centerLabel) !== null && _a !== void 0 ? _a : 'Average value', options.centerAnnotationOffsets);
        }
        // Reliable mouseleave on the SVG root — fires when pointer exits the entire legend
        if (options.onLeave) {
            this.svg.on('mouseleave', () => options.onLeave());
        }
    }
    /**
     * Create a sextant ternary plot
     *
     * @param data - Array of ternary points
     * @param options - Visualization options
     *
     * @throws Error - If showData is true and data contains invalid ternary points
     */
    createSextantPlot(data = [], options = {}) {
        var _a, _b;
        const { center = [1 / 3, 1 / 3, 1 / 3], values = ['#FFFF00', '#B3DCC3', '#01A0C6', '#B8B3D8', '#F11D8C', '#FFB3B3'], showData = true, showCenter = true, showLines = true, labels = ['p₁', 'p₂', 'p₃'], labelPosition = 'corner', colorTarget = 'triangles', cornerLabelOffset = [
            { x: 5, y: 5 },
            { x: 5, y: 5 },
            { x: 5, y: 5 },
        ], } = options;
        if (values.length !== 6) {
            throw new Error('Sextant plot requires exactly 6 color values');
        }
        const plotWidth = this.width - this.margin.left - this.margin.right;
        const plotHeight = this.height - this.margin.top - this.margin.bottom;
        const size = Math.min(plotWidth, plotHeight);
        // Clear previous contents
        this.triangle.selectAll('*').remove();
        this.legend.selectAll('*').remove();
        this.circles.selectAll('*').remove();
        // Generate sextant vertices
        const vertices = TernaryGeometry.ternarySextantVertices(center);
        // Group vertices by sextant id
        const sextantGroups = group(vertices, (d) => d.id);
        // Create a polygon for each sextant
        sextantGroups.forEach((sextantVertices, id) => {
            // Sort vertices by vertex id to ensure proper polygon drawing
            sextantVertices.sort((a, b) => a.vertex - b.vertex);
            const points = sextantVertices
                .map((v) => {
                const [x, y] = this.ternaryToSvgCoords([v.p1, v.p2, v.p3], size);
                return `${x},${y}`;
            })
                .join(' ');
            const colorIndex = Number(id) - 1;
            const classIndex = Number(id) - 1;
            const poly = this.triangle
                .append('polygon')
                .attr('points', points)
                .attr('fill', values[colorIndex])
                .attr('stroke', 'none')
                .attr('data-ecl', classIndex);
            const handlers = options.triangleHandlers;
            if (handlers === null || handlers === void 0 ? void 0 : handlers.mouseover) {
                poly.on('mouseover', (e) => handlers.mouseover(e, values[colorIndex]));
            }
            if (handlers === null || handlers === void 0 ? void 0 : handlers.mouseout) {
                poly.on('mouseout', (e) => handlers.mouseout(e));
            }
        });
        // Draw triangle border and axes
        this.drawTriangleFrame(size, labels, center, showCenter, showLines, labelPosition, colorTarget, null, cornerLabelOffset);
        // Add data points if requested
        if (showData && data.length > 0) {
            const colorTarget = (_a = options.colorTarget) !== null && _a !== void 0 ? _a : 'triangles';
            this.addDataPoints(data, size, options.dataPointHandlers, showCenter, center, undefined, colorTarget);
        }
        if (showCenter) {
            this.addCenterAnnotation(size, center, (_b = options.centerLabel) !== null && _b !== void 0 ? _b : 'Average value', options.centerAnnotationOffsets);
        }
        // Reliable mouseleave on the SVG root — fires when pointer exits the entire legend
        if (options.onLeave) {
            this.svg.on('mouseleave', () => options.onLeave());
        }
    }
    /**
     * Draw the continuous colored triangle on canvas
     */
    drawContinuousTriangle(size, center, hue, chroma, lightness, contrast, spread) {
        if (!this.ctx)
            return;
        const resolution = size;
        const imageData = this.ctx.createImageData(resolution, resolution);
        for (let y = 0; y < resolution; y++) {
            for (let x = 0; x < resolution; x++) {
                // Convert from pixel coordinates to ternary coordinates
                const [p1, p2, p3] = this.svgToTernaryCoords([x, y], resolution);
                // Skip pixels outside the triangle
                if (p1 < 0 || p2 < 0 || p3 < 0 || p1 > 1 || p2 > 1 || p3 > 1) {
                    continue;
                }
                // Calculate color for this point
                const color = ColorMapping.colorMapTricolore([[p1, p2, p3]], center, 100, hue, chroma, lightness, contrast, spread)[0];
                // Parse the hex color
                const r = parseInt(color.rgb.slice(1, 3), 16);
                const g = parseInt(color.rgb.slice(3, 5), 16);
                const b = parseInt(color.rgb.slice(5, 7), 16);
                // Set the pixel color
                const pixelIndex = (y * resolution + x) * 4;
                imageData.data[pixelIndex] = r;
                imageData.data[pixelIndex + 1] = g;
                imageData.data[pixelIndex + 2] = b;
                imageData.data[pixelIndex + 3] = 255;
            }
        }
        this.ctx.putImageData(imageData, 0, 0);
    }
    /**
     * Draw the triangle frame, axes and labels
     */
    drawTriangleFrame(size, labels, center, showCenter, showLines, labelPosition = 'corner', colorTarget = 'triangles', breaks = 4, cornerLabelOffset) {
        // Define triangle corners in ternary coordinates
        // and convert to SVG coordinates
        const corners = [
            [1, 0, 0], // bottom left (p1)
            [0, 1, 0], // top (p2)
            [0, 0, 1], // bottom right (p3)
        ];
        const svgCorners = corners.map((p) => this.ternaryToSvgCoords(p, size));
        // Create the triangle border
        const points = svgCorners.map((p) => p.join(',')).join(' ');
        this.triangle.append('polygon').attr('points', points).attr('fill', 'none').attr('stroke', 'black').attr('stroke-width', 1);
        // Add axis names
        if (labelPosition === 'edge') {
            const labelPositions = [
                // v1 → bottom edge, centred, offset down
                [(svgCorners[0][0] + svgCorners[2][0]) / 2, (svgCorners[0][1] + svgCorners[2][1]) / 2 + 35],
                // v2 → left edge, centred, offset left
                [(svgCorners[0][0] + svgCorners[1][0]) / 2 - 35, (svgCorners[0][1] + svgCorners[1][1]) / 2 - 14],
                // v3 → right edge, centred, offset right
                [(svgCorners[1][0] + svgCorners[2][0]) / 2 + 35, (svgCorners[1][1] + svgCorners[2][1]) / 2 - 14],
            ];
            const rotateValues = [0, -60, 60];
            labels.forEach((label, i) => {
                this.legend
                    .append('text')
                    .attr('x', labelPositions[i][0])
                    .attr('y', labelPositions[i][1])
                    .attr('class', 'em-ternary-axis-title em-legend-label em-ternary-legend-label')
                    .attr('text-anchor', 'middle')
                    .attr('dominant-baseline', 'middle')
                    .attr('transform', `rotate(${rotateValues[i]},${labelPositions[i][0]},${labelPositions[i][1]})`)
                    .text(label);
            });
        }
        else {
            // 'corner'
            const off = cornerLabelOffset !== null && cornerLabelOffset !== void 0 ? cornerLabelOffset : [
                { x: 0, y: 30 },
                { x: 0, y: -5 },
                { x: 12, y: 30 },
            ];
            const labelPositions = [
                [svgCorners[0][0] + off[0].x, svgCorners[0][1] + off[0].y], // p1 bottom-left
                [svgCorners[1][0] + off[1].x, svgCorners[1][1] + off[1].y], // p2 top
                [svgCorners[2][0] + off[2].x, svgCorners[2][1] + off[2].y], // p3 bottom-right
            ];
            labels.forEach((label, i) => {
                this.legend
                    .append('text')
                    .attr('class', 'em-ternary-axis-title em-legend-label em-ternary-legend-label')
                    .attr('x', labelPositions[i][0])
                    .attr('y', labelPositions[i][1])
                    .attr('text-anchor', 'middle')
                    .text(label);
            });
        }
        // Add grid lines and labels for each axis
        // ===============================
        // Grid lines (major + minor)
        // ===============================
        const majorGrid = typeof breaks === 'number' && breaks > 1 ? Array.from({ length: breaks - 1 }, (_, i) => (i + 1) / breaks) : [];
        const minorSubdivisions = 5; // e.g. 5 minor ticks per major
        const minorGrid = [];
        for (let i = 0; i < majorGrid.length; i++) {
            const start = i === 0 ? 0 : majorGrid[i - 1];
            const end = majorGrid[i];
            const step = (end - start) / minorSubdivisions;
            for (let j = 1; j < minorSubdivisions; j++) {
                minorGrid.push(start + j * step);
            }
        }
        const drawGridLine = (a, b, major) => {
            const [x1, y1] = this.ternaryToSvgCoords(a, size);
            const [x2, y2] = this.ternaryToSvgCoords(b, size);
            this.legend
                .append('line')
                .attr('x1', x1)
                .attr('y1', y1)
                .attr('x2', x2)
                .attr('y2', y2)
                .attr('stroke', colorTarget === 'triangles' ? '#555' : '#555')
                .attr('stroke-width', major ? 1 : 0.2)
                .attr('opacity', major ? 0.4 : 0.25)
                .attr('class', major ? 'em-ternary-grid-line' : 'em-ternary-grid-line-minor');
        };
        if (showLines) {
            // --- minor grid first (background) ---
            minorGrid.forEach((v) => {
                // p1
                drawGridLine([v, 0, 1 - v], [v, 1 - v, 0], false);
                // p2
                drawGridLine([0, v, 1 - v], [1 - v, v, 0], false);
                // p3
                drawGridLine([1 - v, 0, v], [0, 1 - v, v], false);
            });
            // --- major grid on top ---
            majorGrid.forEach((v) => {
                // p1 axis (edge p2 = 0)
                const p1Edge = [v, 0, 1 - v];
                drawGridLine(p1Edge, [v, 1 - v, 0], true);
                this.drawAxisTick(p1Edge, [0, -1, +1], size);
                // p2 axis (edge p3 = 0)
                const p2Edge = [1 - v, v, 0];
                drawGridLine([0, v, 1 - v], p2Edge, true);
                this.drawAxisTick(p2Edge, [+1, 0, -1], size);
                // p3 axis (edge p1 = 0)
                const p3Edge = [0, 1 - v, v];
                drawGridLine([1 - v, 0, v], p3Edge, true);
                this.drawAxisTick(p3Edge, [-1, +1, 0], size);
            });
            // ===============================
            // Axis labels
            // ===============================
            const labelStride = this.computeLabelStride(majorGrid.length + 1);
            const labelGrid = majorGrid.filter((_, i) => i % labelStride === 0);
            labelGrid.forEach((val) => {
                // p1 axis
                const line = [this.ternaryToSvgCoords([val, 1 - val, 0], size), this.ternaryToSvgCoords([val, 0, 1 - val], size)];
                this.legend
                    .append('text')
                    .attr('x', line[0][0] - 10)
                    .attr('y', line[0][1] + 3)
                    .attr('text-anchor', 'end')
                    .attr('font-size', '10px')
                    .attr('class', 'em-ternary-axis-tick-label')
                    .text(`${((1 - val) * 100).toFixed(0)}%`);
            });
            labelGrid.forEach((val) => {
                // p2 axis
                const line = [this.ternaryToSvgCoords([0, val, 1 - val], size), this.ternaryToSvgCoords([1 - val, val, 0], size)];
                this.legend
                    .append('text')
                    .attr('x', line[0][0] + 5)
                    .attr('y', line[0][1] - 10)
                    .attr('text-anchor', 'start')
                    .attr('font-size', '10px')
                    .attr('class', 'em-ternary-axis-tick-label')
                    .text(`${((1 - val) * 100).toFixed(0)}%`);
            });
            labelGrid.forEach((val) => {
                // p3 axis
                const line = [this.ternaryToSvgCoords([1 - val, 0, val], size), this.ternaryToSvgCoords([0, 1 - val, val], size)];
                this.legend
                    .append('text')
                    .attr('x', line[0][0] + 10)
                    .attr('y', line[0][1] + 18)
                    .attr('text-anchor', 'middle')
                    .attr('font-size', '10px')
                    .attr('class', 'em-ternary-axis-tick-label')
                    .text(`${((1 - val) * 100).toFixed(0)}%`);
            });
        }
    }
    computeLabelStride(breaks) {
        if (breaks <= 7)
            return 1; // show all
        if (breaks <= 10)
            return 2; // every other
        if (breaks <= 15)
            return 3;
        return Math.ceil(breaks / 4); // cap at ~4 labels per axis
    }
    drawAxisTick(at, normal, size, length = 8) {
        const [x1, y1] = this.ternaryToSvgCoords(at, size);
        const tickEnd = [at[0] + normal[0] * (length / size), at[1] + normal[1] * (length / size), at[2] + normal[2] * (length / size)];
        const [x2, y2] = this.ternaryToSvgCoords(tickEnd, size);
        this.legend
            .append('line')
            .attr('x1', x1)
            .attr('y1', y1)
            .attr('x2', x2)
            .attr('y2', y2)
            .attr('stroke-width', 1)
            .attr('opacity', 0.4)
            .attr('class', 'em-ternary-axis-tick');
    }
    /**
     * Add data points to the visualization
     */
    addDataPoints(data, size, handlers, showCenter, center, colors, colorTarget = 'triangles') {
        const closed = CompositionUtils.close([...data]);
        // Validate data (this will throw an error if invalid)
        CompositionUtils.validateTernaryPoints(closed);
        closed.forEach((p, i) => {
            if (p) {
                const [x, y] = this.ternaryToSvgCoords(p, size);
                const fill = colorTarget === 'points' && colors && colors[i] && colors[i].rgb ? colors[i].rgb : 'black';
                const c = this.circles
                    .append('circle')
                    .datum({ point: p, index: i })
                    .attr('cx', x)
                    .attr('cy', y)
                    .attr('r', 2)
                    .attr('fill', fill)
                    .attr('opacity', colorTarget === 'points' ? 1 : 0.7)
                    .attr('class', 'em-ternary-legend-data-point');
                if (handlers === null || handlers === void 0 ? void 0 : handlers.mouseover) {
                    c.on('mouseover', (e) => handlers.mouseover(e, c.datum()));
                }
                if (handlers === null || handlers === void 0 ? void 0 : handlers.mousemove) {
                    c.on('mousemove', (e) => handlers.mousemove(e, c.datum()));
                }
                if (handlers === null || handlers === void 0 ? void 0 : handlers.mouseout) {
                    c.on('mouseout', (e) => handlers.mouseout(e, c.datum()));
                }
            }
        });
        if (showCenter) {
            const centerSvg = this.ternaryToSvgCoords(center, size);
            const [cx, cy] = centerSvg;
            //LINES
            const guides = [
                // p₁ = c1 → bottom edge (p2 = 0)
                [[center[0], 0, 1 - center[0]], center],
                // p₂ = c2 → LEFT edge (p3 = 0)
                [[1 - center[1], center[1], 0], center],
                // p₃ = c3 → right edge (p1 = 0)
                [[0, 1 - center[2], center[2]], center],
            ];
            guides.forEach(([a, b], i) => {
                const [x1, y1] = this.ternaryToSvgCoords(a, size);
                const [x2, y2] = this.ternaryToSvgCoords(b, size);
                this.circles
                    .append('line')
                    .attr('x1', x1)
                    .attr('y1', y1)
                    .attr('x2', x2)
                    .attr('y2', y2)
                    .attr('stroke', colorTarget === 'triangles' ? 'white' : '#000')
                    .attr('stroke-width', colorTarget === 'triangles' ? 1 : 1)
                    .attr('opacity', colorTarget === 'triangles' ? 0.9 : 1)
                    .attr('class', 'em-ternary-center-line');
                // Add label showing the center value for this component
                const centerValue = Math.round(center[i] * 100);
                // Use the edge point (x1, y1)
                const labelX = x1;
                const labelY = y1;
                // Calculate line angle
                const lineAngle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);
                // Adjust rotation for labels 2 and 3 (flip 180 degrees)
                let rotation = lineAngle;
                // Offset distance - make label 1 further from edge
                const offsetDistance = i === 0 ? 25 : 15;
                let offsetX = offsetDistance * Math.cos((lineAngle * Math.PI) / 180);
                let offsetY = offsetDistance * Math.sin((lineAngle * Math.PI) / 180);
                //left
                if (i === 1) {
                    offsetX += -5;
                    offsetY += 8;
                }
                //bottom
                if (i === 0) {
                    rotation = lineAngle + 180;
                    offsetX += 14;
                    offsetY += 10;
                }
                //right
                if (i === 2) {
                    rotation = lineAngle + 180;
                    offsetX += 5;
                    offsetY += 10;
                }
                this.circles
                    .append('text')
                    .attr('x', labelX + offsetX)
                    .attr('y', labelY + offsetY)
                    .attr('text-anchor', 'middle')
                    .attr('dominant-baseline', 'middle')
                    .attr('fill', colorTarget === 'triangles' ? 'white' : '#000')
                    .attr('transform', `rotate(${rotation}, ${labelX + offsetX}, ${labelY + offsetY})`)
                    .attr('class', 'em-ternary-center-line-label')
                    .text(`${centerValue}%`);
            });
            this.circles
                .append('circle')
                .attr('cx', cx)
                .attr('cy', cy)
                .attr('r', 3)
                .attr('fill', 'black')
                .attr('stroke', 'white')
                .attr('class', 'em-ternary-center-point');
        }
    }
    /**
     * Add a curved annotation from the ternary center point
     * to the top of the legend area explaining what it is.
     */
    addCenterAnnotation(size, center, text = 'Average value', offsets = { labelX: 70, labelY: 20, curveX: 10, curveY: 0 }) {
        const [cx, cy] = this.ternaryToSvgCoords(center, size);
        // Target point above the triangle (inside legend space)
        const tx = cx + offsets.labelX; //offset to the right
        const ty = 35 + offsets.labelY; //offset down
        // Control point for curvature (pulls curve upward)
        const mx = cx + offsets.curveX;
        const my = cy - offsets.curveY;
        const path = `
        M ${cx},${cy}
        Q ${mx},${my} ${tx},${ty}
    `;
        const g = this.circles.append('g').attr('class', 'em-ternary-center-annotation');
        // Curved guide line
        g.append('path').attr('d', path).attr('fill', 'none').attr('class', 'em-ternary-annotation-line');
        // Label
        g.append('text')
            .attr('x', tx)
            .attr('y', ty - 6)
            .attr('class', 'em-ternary-center-annotation-label')
            .text(text);
    }
    /**
     * Convert ternary coordinates to SVG coordinates
     */
    ternaryToSvgCoords(p, size) {
        const [x, y] = TernaryGeometry.ternaryToCartesian(p);
        return [x * size, size - y * size];
    }
    /**
     * Convert SVG coordinates to ternary coordinates
     */
    svgToTernaryCoords(point, size) {
        return TernaryGeometry.cartesianToTernary(point[0] / size, 1 - point[1] / size);
    }
}
